/**
 * Phase 4 — Git SyncTarget outbound flush + inbound apply (JS driver).
 *
 * ONE serialization pipeline for every flag-enrolled workspace:
 *   SQLite (workspace_db_load_snapshot) → snapshotToFileMap → disk.
 *
 * Targets:
 *  - git-storage workspaces → workspace.rootPath (the user-chosen repo dir)
 *  - local/cloud workspaces → AppLocalData/workspace-mirror/{id} (the GitHub
 *    sync mirror), replacing workspaceMirror.writeMirror's nested layout with
 *    the flat yamlWorkspace layout (one-time wipe + regeneration per mirror).
 *
 * Only changed files are rewritten and orphaned managed files are deleted
 * (slug renames leave no stale file; deletions round-trip as file removals).
 * After a successful flush the git target acks the outbox up to the seq read
 * BEFORE the snapshot load (sync_state (ws,'git').last_acked_outbox_seq).
 *
 * Inbound: an external change on disk is parsed (loadGitSnapshot) and applied
 * to SQLite via workspace_db_apply_external_snapshot — full_rebuild for fresh
 * clones / older-commit restores, diff-apply (with tombstones + outbox ops)
 * for ordinary external edits.
 *
 * Phase 3's Rust engine will later emit sync:git-flush-needed; it should call
 * the exported flushWorkspaceToDisk from its subscriber — same entry point.
 */
import { isTauri as coreIsTauri } from '@tauri-apps/api/core';
import { snapshotToFileMap } from '../utils/yamlWorkspace/serialize.js';
import { normalizeRootPath } from '../utils/yamlWorkspace/paths.js';
import { normalizeSnapshot } from './localWorkspaceStore.js';
import { loadGitSnapshot, registerWorkspaceScope } from './gitWorkspaceStore.js';
import { mirrorAbsDir } from './workspaceMirror.js';
import {
  loadDbSnapshot,
  applyExternalDbSnapshot,
  ackGitFlush,
  getOutboxMaxSeq,
} from './syncStore.js';
import { workspaceDbKey } from './workspaceDbMigration.js';
import { isGitWorkspace } from '../utils/workspaceStorage.js';
import { readTextSafe, writeWorkspaceFiles } from './gitFileWriter.js';
import { keepRepoIdentity } from './git/repoIdentity.js';
import { blockedByUnreadable } from './gitUnreadable.js';
import { useGitSyncStore } from '../store/gitSyncStore';
import { projectBlockFor } from '../scope/projectManifestBlock';

/** Marker: mirrors already regenerated to the flat yamlWorkspace layout. */
const FLAT_LAYOUT_KEY = 'apiboo-mirror-flat-layout-v1';

function isTauri() {
  try {
    return coreIsTauri();
  } catch {
    return typeof window !== 'undefined' && window.__TAURI__ !== undefined;
  }
}

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

/* ── flat-layout regeneration marker ─────────────────────────────────────── */

function readFlatLayoutSet() {
  try {
    const raw = localStorage.getItem(FLAT_LAYOUT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function markFlatLayout(workspaceKey) {
  try {
    const set = readFlatLayoutSet();
    set.add(String(workspaceKey));
    localStorage.setItem(FLAT_LAYOUT_KEY, JSON.stringify([...set]));
  } catch {
    /* storage unavailable — regen just runs again next time (idempotent) */
  }
}

/* ── fs helpers (absolute paths) ─────────────────────────────────────────── */

/** Remove everything except .git so a full rewrite starts clean. */
async function clearDirExceptGit(fs, root) {
  let entries = [];
  try {
    entries = await fs.readDir(root);
  } catch {
    return;
  }
  for (const entry of entries) {
    if (!entry?.name || entry.name === '.git') continue;
    try {
      await fs.remove(`${root}/${entry.name}`, { recursive: true });
    } catch {
      /* best-effort */
    }
  }
}

/* ── outbound flush ──────────────────────────────────────────────────────── */

/** Absolute target directory for a workspace's YAML tree. */
export async function flushTargetDir(workspace) {
  if (isGitWorkspace(workspace)) return normalizeRootPath(workspace.rootPath);
  return normalizeRootPath(await mirrorAbsDir(workspaceDbKey(workspace)));
}

/**
 * Flush the SQLite snapshot of an enrolled workspace to its YAML tree.
 * Writes only changed files, deletes orphaned managed files, then acks the
 * git target's outbox checkpoint. `fullRewrite` wipes (except .git) first —
 * used for the one-time nested→flat mirror regeneration.
 *
 * @returns {{ written: number, deleted: number, dir: string } | null}
 */
export async function flushWorkspaceToDisk(workspace, { fullRewrite = false } = {}) {
  if (!isTauri()) return null;
  const id = workspaceDbKey(workspace);
  if (!id) return null;

  const git = isGitWorkspace(workspace);
  const dir = await flushTargetDir(workspace);
  if (!dir) return null;

  // Mirror workspaces regenerate once: the old workspaceMirror nested layout
  // is derived data — wipe + rewrite as the flat yamlWorkspace layout. The
  // next GitHub-sync commit is the single "restructure" commit.
  let rewrite = fullRewrite;
  if (!git && !readFlatLayoutSet().has(String(id))) rewrite = true;

  // Read the ack watermark BEFORE loading the snapshot: everything at or
  // below this seq is definitely represented in what we are about to write.
  const maxSeq = await getOutboxMaxSeq(id).catch(() => null);

  const snapshot = normalizeSnapshot(await loadDbSnapshot(id));
  const { files } = snapshotToFileMap(snapshot, {
    id,
    name: workspace?.name || snapshot.workspaceName || '',
    project: projectBlockFor(id), // card 2.2
  });

  // The mirror lives in app data, already in the static fs:scope; registering
  // it is refused by register_workspace_scope (card 352356b4 S5).
  if (git) await registerWorkspaceScope(dir);
  const fs = await fsModule();

  if (rewrite) {
    // The wipe must not cost the repository its identity (repoIdentity.js).
    const before = await readTextSafe(fs, `${dir}/apiboo.yaml`);
    if (files['apiboo.yaml']) files['apiboo.yaml'] = keepRepoIdentity(before, files['apiboo.yaml']);
    await clearDirExceptGit(fs, dir);
  }

  // Only changed bytes are written, every write is recorded as our own (no
  // watcher echo), orphans in the managed roots are deleted — gitFileWriter.js.
  const { written, deleted } = await writeWorkspaceFiles(fs, dir, files, { rewrite });

  if (!git) markFlatLayout(id);

  // Ack after a successful flush; failure to ack must not fail the flush
  // (outbox rows just stay pending until the next one).
  if (typeof maxSeq === 'number' && maxSeq > 0) {
    await ackGitFlush(id, maxSeq).catch((err) => {
      console.warn('[gitFlush] outbox ack failed (will retry next flush):', err);
    });
  }

  return { written, deleted, dir };
}

/* ── debounced flush scheduling (post-save trigger) ──────────────────────── */

/** The rail dot and the Git page show "Changes waiting" at the save itself; the status read after the flush confirms it. */
function markChangedNow(workspace) {
  if (workspace?.id != null) useGitSyncStore.getState().markChanged(String(workspace.id));
}

/** @type {Map<string, { timer: any, workspace: object }>} */
const pendingFlushes = new Map();
const FLUSH_DEBOUNCE_MS = 800;

/**
 * Debounced flush after a successful SQLite save. Coalesces bursts of saves
 * into one disk write. Fire-and-forget: flush failures are logged, never
 * surfaced to the save path (SQLite is the source of truth).
 */
export function scheduleGitFlush(workspace, delay = FLUSH_DEBOUNCE_MS) {
  if (!isTauri()) return;
  const id = workspaceDbKey(workspace);
  if (!id) return;

  const existing = pendingFlushes.get(id);
  if (existing) clearTimeout(existing.timer);
  markChangedNow(workspace);
  const timer = setTimeout(() => {
    pendingFlushes.delete(id);
    flushWorkspaceToDisk(workspace)
      .then(async (res) => {
        // The files changed: the rail dot, the status bar and the Git page learn
        // it now, not at the next poll (owner, 06.10.2026: "it's like not reactive").
        if (!res || (!res.written && !res.deleted)) return;
        const { refreshGitStatus } = await import('./git/gitSyncStatus');
        await refreshGitStatus(workspace);
      })
      .catch((err) => {
        console.warn('[gitFlush] flush failed:', err);
      });
  }, delay);
  pendingFlushes.set(id, { timer, workspace });
}

/* ── inbound apply ───────────────────────────────────────────────────────── */

/**
 * Apply an externally-produced snapshot (parsed YAML tree) to SQLite.
 * Strips device-local fields first: selectedEnvironmentId is written into
 * apiboo.yaml for legacy-loader compatibility, but per README §6 it is a
 * device pref and must never be clobbered by a pull/import.
 */
export async function applyExternalWorkspaceSnapshot(
  workspace,
  snapshot,
  { fullRebuild = false } = {}
) {
  const id = workspaceDbKey(workspace);
  if (!id) return;
  const external = { ...snapshot };
  delete external.selectedEnvironmentId;
  delete external.id; // path/manifest identity, not row data
  await applyExternalDbSnapshot(id, external, fullRebuild);
}

/** @type {Map<string, { timer: any, promise: Promise, resolve: Function, reject: Function }>} */
const pendingApplies = new Map();
const APPLY_DEBOUNCE_MS = 300;

/**
 * Debounced inbound apply for file-watcher events on an enrolled git-storage
 * workspace: re-reads the on-disk YAML tree and diff-applies it to SQLite.
 * Multiple watcher events within the window share one apply; the returned
 * promise resolves when that apply completes.
 */
export function scheduleExternalSnapshotApply(workspace, delay = APPLY_DEBOUNCE_MS) {
  const id = workspaceDbKey(workspace);
  const root = normalizeRootPath(workspace?.rootPath);
  if (!id || !root) return Promise.resolve();

  const run = async () => {
    const snap = await loadGitSnapshot(root);
    // A file that did not load would count as deleted — apply nothing until
    // every file reads (gitUnreadable.js).
    if (blockedByUnreadable(snap)) return;
    await applyExternalWorkspaceSnapshot(workspace, snap, { fullRebuild: false });
  };

  const existing = pendingApplies.get(id);
  if (existing) {
    clearTimeout(existing.timer);
    existing.timer = setTimeout(existing.fire, delay);
    return existing.promise;
  }

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  const entry = { timer: null, promise, resolve, reject, fire: null };
  entry.fire = () => {
    pendingApplies.delete(id);
    run().then(resolve, reject);
  };
  entry.timer = setTimeout(entry.fire, delay);
  pendingApplies.set(id, entry);
  return promise;
}
